import {ref} from 'vue';
import {FetchError} from 'ofetch';
import {useLogger} from '#imports';
import {readonlyState} from '@malt/utils';

export type Thumbnails = {
    highResolutionUrl: string;
    mediumResolutionUrl?: string;
    lowResolutionUrl?: string;
};

type VideoInfos = {
    title: string;
    thumbnails: Thumbnails;
};

export enum VideoErrorType {
    INVALID_URL = 'INVALID_URL',
    VIDEO_NOT_FOUND = 'VIDEO_NOT_FOUND',
    EMBEDDING_NOT_ALLOWED = 'EMBEDDING_NOT_ALLOWED',
}
export type VideoError = {
    error: VideoErrorType;
};

export type VideoResult = VideoInfos | VideoError;

class InvalidVideoURLError extends Error {
    constructor(url: string) {
        super(`Invalid video url: ${url}`);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

class VideoNotFoundError extends Error {
    constructor(url: string) {
        super(`No video found for ${url}`);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

class VideoEmbeddingPermissionDenied extends Error {
    constructor(url: string) {
        super(`Embedding is not allowed on video ${url}`);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export function useVideoInfos() {
    const logger = useLogger();

    const loading = ref(false);

    async function getVideoInfos(url: string): Promise<VideoResult | undefined> {
        try {
            loading.value = true;
            if (url.includes('youtu') && !url.includes('playlist')) {
                return await getYoutubeInfo(url);
            }
            if (url.includes('vimeo')) {
                return await getVimeoInfo(url);
            }
        } catch (e) {
            if (e instanceof InvalidVideoURLError) {
                return {
                    error: VideoErrorType.INVALID_URL,
                };
            }
            if (e instanceof VideoNotFoundError) {
                return {
                    error: VideoErrorType.VIDEO_NOT_FOUND,
                };
            }
            if (e instanceof VideoEmbeddingPermissionDenied) {
                return {
                    error: VideoErrorType.EMBEDDING_NOT_ALLOWED,
                };
            }
            logger.error('Cannot get video infos', e);
            return undefined;
        } finally {
            loading.value = false;
        }
    }

    // YOUTUBE

    type YoutubeAPIResponse = {
        items: {
            snippet: {
                title: string;
                thumbnails: {
                    default: {url: string};
                    medium: {url: string};
                    high: {url: string};
                    standard: {url: string};
                    maxres: {url: string};
                };
            };
        }[];
    };

    async function getYoutubeInfo(url: string): Promise<VideoInfos> {
        const id = extractYoutubeIdFromUrl(url);
        if (!id) {
            throw new InvalidVideoURLError(url);
        }

        try {
            const youtubeInfo = await $fetch<YoutubeAPIResponse>(`/youtube/info?id=${id}`);
            const snippet = youtubeInfo.items[0].snippet;

            const lowRes = snippet.thumbnails.medium?.url ?? snippet.thumbnails.default.url;
            const mediumRes = snippet.thumbnails.high?.url ?? lowRes;
            const highRes = snippet.thumbnails.maxres?.url ?? snippet.thumbnails.standard?.url ?? mediumRes;

            return {
                title: snippet.title,
                thumbnails: {
                    highResolutionUrl: highRes,
                    mediumResolutionUrl: mediumRes,
                    lowResolutionUrl: lowRes,
                },
            };
        } catch (error) {
            if (error instanceof FetchError && error.statusCode === 404) {
                throw new VideoNotFoundError(url);
            }
            throw error;
        }
    }

    // VIMEO

    type VimeoAPIResponse = {
        title: string;
        thumbnail_url: string;
        domain_status_code?: number;
    };

    async function getVimeoInfo(url: string): Promise<VideoInfos> {
        const {id} = extractVimeoIdFromUrl(url);
        if (!id) {
            throw new InvalidVideoURLError(url);
        }
        try {
            // See https://developer.vimeo.com/api/oembed/videos
            const videoInfo: VimeoAPIResponse = await $fetch<VimeoAPIResponse>(
                `https://vimeo.com/api/oembed.json?url=${url.trim()}&width=1920&height=1920`,
            );

            if (videoInfo?.domain_status_code === 403) {
                throw new VideoEmbeddingPermissionDenied(url);
            }

            const thumbnailUrl = videoInfo.thumbnail_url.split('?')[0];
            return {
                title: videoInfo.title,
                thumbnails: {
                    highResolutionUrl: thumbnailUrl,
                    mediumResolutionUrl: thumbnailUrl,
                    lowResolutionUrl: thumbnailUrl,
                },
            };
        } catch (error) {
            if (error instanceof FetchError && error.statusCode === 404) {
                throw new VideoNotFoundError(url);
            }
            if (error instanceof FetchError) {
                throw new VideoEmbeddingPermissionDenied(url);
            }
            throw error;
        }
    }

    return {
        loading: readonlyState(loading),
        getVideoInfos,
    };
}

export function extractYoutubeIdFromUrl(url: string): string | undefined {
    const regExp = /(youtu\.be\/|youtube\.com\/(watch\?(.*&)?v=|(embed|v)\/))([^\\?&"'>]+)/;
    const match = url.match(regExp);
    return match && match[5].length === 11 ? match[5] : undefined;
}

export function extractVimeoIdFromUrl(url: string): {id?: string; hash?: string} {
    const regExp = /(https:\/\/)?vimeo.com\/(\d+)(\/(\w+))?/;
    const match = url.match(regExp);

    if (!match || match.length <= 1) {
        return {};
    }

    return {id: match[2], hash: match[4]};
}
